import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "selectedCount",
    "message",
    "characterCount",
    "sendButton",
    "modalCount",
    "modalCredits",
    "modalMessage",
    "totalCost",
    "form",
    "creditWarning"
  ]

  static values = {
    availableCredits: Number,
    creditsPerSms: { type: Number, default: 1 },
    costPrefix: { type: String, default: "Total cost: " },
    costSuffix: { type: String, default: " credits" }
  }

  connect() {
    this.updateSelectedCount()
    this.updateCharacterCount()
  }

  // Checkbox selection
  toggleAll(event) {
    const isChecked = event.target.checked
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = isChecked
      this.updateRowStyle(checkbox)
    })
    this.updateSelectedCount()
  }

  toggleCustomer(event) {
    this.updateSelectAllState()
    this.updateRowStyle(event.target)
    this.updateSelectedCount()
  }

  updateSelectAllState() {
    const total = this.checkboxTargets.length
    const checked = this.checkboxTargets.filter(cb => cb.checked).length

    if (checked === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checked === total) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  updateRowStyle(checkbox) {
    const row = checkbox.closest("tr")
    if (checkbox.checked) {
      row.classList.add("bg-purple-100", "border-l-4", "border-purple-500")
      row.classList.remove("hover:bg-purple-50")
    } else {
      row.classList.remove("bg-purple-100", "border-l-4", "border-purple-500")
      row.classList.add("hover:bg-purple-50")
    }
  }

  updateSelectedCount() {
    const count = this.checkboxTargets.filter(cb => cb.checked).length
    this.selectedCountTarget.textContent = count
    this.updateTotalCost(count)
    this.updateSendButtonState()
  }

  updateTotalCost(count) {
    if (this.hasTotalCostTarget) {
      const totalCredits = count * this.creditsPerSmsValue
      this.totalCostTarget.textContent = `${this.costPrefixValue}${totalCredits}${this.costSuffixValue}`
    }
  }

  // Character counter
  updateCharacterCount() {
    const length = this.messageTarget.value.length
    const counter = this.characterCountTarget

    counter.textContent = `${length}/160`

    // Remove all color classes
    counter.classList.remove("text-green-600", "text-yellow-600", "text-red-600")

    // Apply appropriate color
    if (length <= 119) {
      counter.classList.add("text-green-600")
    } else if (length <= 150) {
      counter.classList.add("text-yellow-600")
    } else {
      counter.classList.add("text-red-600")
    }

    this.updateSendButtonState()
  }

  updateSendButtonState() {
    const selectedCount = this.checkboxTargets.filter(cb => cb.checked).length
    const messageLength = this.messageTarget.value.length
    const isValid = selectedCount > 0 && messageLength > 0 && messageLength <= 160

    this.sendButtonTarget.disabled = !isValid

    if (isValid) {
      this.sendButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    } else {
      this.sendButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  // Prepare modal content before opening
  prepareModal(event) {
    event.preventDefault()

    const selectedCount = this.checkboxTargets.filter(cb => cb.checked).length
    const totalCredits = selectedCount * this.creditsPerSmsValue

    this.modalCountTarget.textContent = selectedCount

    if (this.hasModalCreditsTarget) {
      this.modalCreditsTarget.textContent = totalCredits
    }

    if (this.hasModalMessageTarget && this.hasMessageTarget) {
      this.modalMessageTarget.textContent = `"${this.messageTarget.value}"`
    }

    // Show/hide credit warning
    if (this.hasAvailableCreditsValue && totalCredits > this.availableCreditsValue) {
      this.creditWarningTarget.classList.remove("hidden")
    } else {
      this.creditWarningTarget.classList.add("hidden")
    }

    // Dispatch event to trigger modal open
    this.dispatch("modalReady")
  }

  confirmSend() {
    this.formTarget.submit()
  }
}
